import React, {useMemo} from 'react';
import Button from '../components/button';
import InfoIcon from '../components/info-icon';
import {getRandomDashboardHeroImage} from "../helpers/dashboardHeroImages";
import './data-onboarding.scss';

export type WelcomeProps = {
    onRequestData: (e: React.MouseEvent) => void,
    onShowUploadData: () => void
}

export default function Welcome({onRequestData, onShowUploadData}: WelcomeProps) {
    const onboardingHeroImage = useMemo(() => getRandomDashboardHeroImage(), []);

    return (
        <section className="data-onboarding" aria-labelledby="welcome-title">
            <div className="data-onboarding__content">
                <h1 className="data-onboarding__headline" id="welcome-title">Welcome to BirdMap Explorer! Let&apos;s get started!</h1>
                <div className="data-onboarding__copy">
                    <p>BirdMap Explorer lets you visualize your birding adventures on a map of the world. You can view your life list, your year list, or all the places you&apos;ve ever gone birding, based on your eBird sightings.</p>
                    <p>To get started, you&apos;ll need to download your eBird data as a ZIP file. Click the button below to go to eBird, then click the &ldquo;Request my Observations&rdquo; button. Within a few minutes, you should receive an email with a link to your data file.</p>
                </div>
                <div className="data-onboarding__welcome-actions">
                    <div className="data-onboarding__cta-row">
                        <Button btnClass="primary" onClick={onRequestData}>Request my Data</Button>
                        <Button btnClass="secondary" onClick={onShowUploadData}>I have a data file</Button>
                    </div>
                    <a href="https://ebird.org/about" target="_blank" rel="noreferrer" className="data-onboarding__helper-link data-onboarding__helper-link--centered">
                        <InfoIcon className="data-onboarding__helper-icon" />
                        What&apos;s eBird?
                    </a>
                </div>
                <div className="data-onboarding__privacy">
                    <InfoIcon className="data-onboarding__privacy-icon" />
                    <span>
                        <strong className="mb-1">Your data stays private.</strong>
                        <span>We never store your checklists; all your data stays on your device.</span>
                    </span>
                </div>
            </div>
            <aside
                className="data-onboarding__hero"
                aria-label="Bird in a natural habitat"
                style={{'--data-onboarding-hero-image': `url("${onboardingHeroImage}")`} as React.CSSProperties}
            >
                <div className="data-onboarding__hero-card">
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">1</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Request Your eBird Observations</strong>
                            <span className="data-onboarding__feature-copy">Request your personal eBird data export and check your email for a ZIP file.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">2</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Sync Your Sightings</strong>
                            <span className="data-onboarding__feature-copy">Drag or select the ZIP file here and BirdMap Explorer will read your eBird history. Your sightings stay on your computer.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">3</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Explore Your World</strong>
                            <span className="data-onboarding__feature-copy">View your birding history as interactive maps, species / family checklists, and customized year views.</span>
                        </span>
                    </div>
                </div>
            </aside>
        </section>
    )
}
